import React from "react";
import { Add, EditOutlined, InfoOutlined, Sync } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { localizeDateShort } from "utils/dateFormats";
import { useLazyListBranchTallyUploadsQuery } from "store/services/branchTallyUploadApi";
import { useAddMissingBranchTallyUploadsMutation } from "store/services/branchTallyUploadApi";
import { useAppMeta, useTablePagination } from "hooks";

const BranchTallyUploadListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const [fetch, { data, isLoading, isFetching, error }] = useLazyListBranchTallyUploadsQuery();
  const [addMissingBranches, { isLoading: addingMissingBranches }] = useAddMissingBranchTallyUploadsMutation();
  const search = searchParams.get("search") || "";
  const activeTab = searchParams.get("tab") || "Active";
  const uploadTabs = [{ label: "Active", value: "Active" }, { label: "Inactive", value: "Inactive" }];
  React.useEffect(() => { fetch({ active: activeTab === "Active", page: currentPage, limit: perPage, ...(search && { keyword: search }) }); }, [activeTab, fetch, currentPage, perPage, search]);
  const handleAddMissingBranches = async () => {
    try {
      const result = await addMissingBranches().unwrap();
      toast.success(result.createdCount ? `${result.createdCount} branch(es) added` : "No new branches to add");
      fetch({ active: activeTab === "Active", page: currentPage, limit: perPage, ...(search && { keyword: search }) });
    } catch (err) {
      toast.error(err?.data?.message || "Could not add branches");
    }
  };
  const columns = [
    { name: "Branch", selector: (row) => row.branch?.name, cell: (row) => <Link className="hover:underline" to={`/app/crm/reports/branch-tally-upload/${row._id}/detail`}>{row.branch?.name || "N/A"}</Link> },
    { name: "Uploaded By", selector: (row) => row.uploadedBy?.name || row.uploadedBy?.email || "N/A" },
    { name: "Updated By", selector: (row) => row.updatedBy?.name || row.updatedBy?.email || "N/A" },
    { name: "Created Date", selector: (row) => localizeDateShort(row.createdAt) },
    { name: "Updated Date", selector: (row) => row.updatedAt ? localizeDateShort(row.updatedAt) : "-" },
    { name: "Attachments", selector: (row) => row.attachments?.filter((attachment) => attachment.isActive).length || 0 },
    { name: "Actions", right: true, cell: (row) => <><Link to={`/app/crm/reports/branch-tally-upload/${row._id}/detail`}><IconButton size="small"><InfoOutlined /></IconButton></Link><Link to={`/app/crm/reports/branch-tally-upload/${row._id}/edit`}><IconButton size="small"><EditOutlined /></IconButton></Link></> },
  ];
  useAppMeta(`${activeTab} Branch Tally Upload`);
  return (
    <BreadcrumbLayout
      breadcrumbs={[{ title: "Reports", path: "/app/crm/reports/list" },{ title: "Branch Tally Upload" }]}
      headerActions={<PageActions onAddMissingBranches={handleAddMissingBranches} addingMissingBranches={addingMissingBranches} remainingBranchCount={data?.remainingBranchCount || 0} />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={uploadTabs} activeTab={activeTab} />
        <TableFilter searchLabel="Branch" disabled={isLoading || isFetching} />
        <AppTable
          columns={columns}
          data={data?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={data?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};
export default BranchTallyUploadListScreen;

const PageActions = ({ onAddMissingBranches, addingMissingBranches, remainingBranchCount }) => (
  <div className="flex gap-2">
    <Button variant="outlined" startIcon={<Sync />} onClick={onAddMissingBranches} disabled={addingMissingBranches || remainingBranchCount === 0}>
      Add Branches ({remainingBranchCount})
    </Button>
    <Link to="/app/crm/reports/branch-tally-upload/create">
      <Button variant="contained" startIcon={<Add />}>
        Add Branch Tally Uploads
      </Button>
    </Link>
  </div>
);
